import React, { useCallback, useMemo, useRef, useState } from "react";
import StatWrapper from "../shared/StatWrapper";
import Heading from "hardcover-ui/components/Heading";
import Text from "hardcover-ui/components/Text";
import { useChartTheme } from "../contexts/ChartThemeContext";
import { usePage } from "@inertiajs/react";
import { UserStatsPageProps } from "pages/clientverse/users/stats/index.props";
import LocaleNumber from "components/ui/LocaleNumber";
import { curveMonotoneX, line } from "d3";
import {
  StatsSerializersPagePoint,
  StatsSerializersPagesRead,
} from "types/serializers";
import { parsedDateToDate } from "lib/dateUtils";
import LayerCake, { useLayerCake } from "components/viz/LayerCake";
import AxisX from "components/viz/AxisX";
import { tickFormatForDateDomain } from "components/viz/utils";

interface PagesReadStatProps {
  size?: "sm" | "md" | "lg" | "xl";
}

type PagesReadGraphType = "pagesRead" | "hoursAsPages" | "totalPages";

function Tooltip({ points }: { points: StatsSerializersPagePoint[] }) {
  const { getMonotheme, getTheme, theme } = useChartTheme();
  const {
    domainScale,
    rangeScale,
    padding,
    height,
    range,
    domainBand,
    active,
    setActive,
  } = useLayerCake();
  const hideTimeout = useRef(null);

  const hideTooltip = () => {
    hideTimeout.current = setTimeout(() => {
      setActive(null);
    }, 100);
  };

  if (!points || points.length === 0) return null;

  return (
    <g transform={`translate(${padding.left},${padding.top})`} className="z-10">
      {points.map((point) => {
        if (!point || !point.label) return null;
        return (
          <g key={point.label}>
            <rect
              x={
                domainScale(parsedDateToDate(point.label).getTime()) -
                domainBand.bandwidth() / 2
              }
              y={rangeScale(range[1])}
              width={domainBand.bandwidth()}
              height={height - padding.top - padding.bottom}
              fill="transparent"
              onMouseEnter={() => {
                if (hideTimeout.current) {
                  clearTimeout(hideTimeout.current);
                  hideTimeout.current = null;
                }
                setActive(point);
              }}
              onMouseLeave={hideTooltip}
            />

            {active && active.label === point.label && (
              <line
                x1={domainScale(parsedDateToDate(point.label).getTime())}
                y1={rangeScale(range[0])}
                x2={domainScale(parsedDateToDate(point.label).getTime())}
                y2={rangeScale(range[1])}
                stroke={theme === "colorful" ? getTheme(8) : getMonotheme(8)}
                strokeWidth="1"
                strokeDasharray="2 2"
              />
            )}
          </g>
        );
      })}
    </g>
  );
}

function TooltipText({ visibleStats }: { visibleStats: PagesReadGraphType[] }) {
  const { active, domainScale, domain } = useLayerCake();
  if (!active) {
    return false;
  }

  const formatTick = useMemo(() => {
    return tickFormatForDateDomain(domain);
  }, [domain]);

  return (
    <p
      className="absolute bg-gray-200 dark:bg-gray-800 p-1 rounded-sm shadow-md text-nowrap transition-all pointer-events-none"
      style={{
        left: domainScale(parsedDateToDate(active.label).getTime()),
        top: -40,
      }}
    >
      <b>{formatTick(active.label)}</b>
      <br />
      {visibleStats.includes("pagesRead") && (
        <>{active.pagesRead?.toLocaleString() || 0} pages</>
      )}
      {visibleStats.includes("totalPages") && <> + </>}
      {visibleStats.includes("hoursAsPages") && (
        <>{active.hoursAsPages?.toLocaleString() || 0} page hours</>
      )}
      {visibleStats.includes("totalPages") && <> = </>}
      {visibleStats.includes("totalPages") && (
        <>{active.totalPages?.toLocaleString() || 0} pages</>
      )}
    </p>
  );
}

function PagesReadGraphLines({
  data,
  visibleStats,
  colors,
}: {
  data: StatsSerializersPagesRead;
  visibleStats: PagesReadGraphType[];
  colors: Record<string, { color: string; mono: string }>;
}) {
  const { domainScale, rangeScale, padding } = useLayerCake();
  const { theme } = useChartTheme();

  const createPath = useCallback(
    (dataPoints: StatsSerializersPagePoint[], type: PagesReadGraphType) => {
      if (!dataPoints || dataPoints.length < 2) return "";

      const lineGenerator = line<StatsSerializersPagePoint>()
        .x((d) => domainScale(parsedDateToDate(d?.label || "").getTime()))
        .y((d) => rangeScale(d?.[type] || 0))
        .curve(curveMonotoneX);

      return lineGenerator(dataPoints) || "";
    },
    [domainScale, rangeScale]
  );

  const paths = useMemo(() => {
    if (!data?.points || data.points.length === 0) return [];

    return visibleStats.map((type) => {
      return {
        type,
        path: createPath(data.points, type as PagesReadGraphType),
        color: theme === "colorful" ? colors[type]?.color : colors[type]?.mono,
      };
    });
  }, [visibleStats, data?.points, domainScale, rangeScale, theme, colors]);

  if (!paths.length) return null;

  return (
    <g transform={`translate(${padding.left},${padding.top})`}>
      {paths.map((path) => {
        if (!path.path) return null;
        return (
          <g key={path.type}>
            <path
              d={path.path}
              fill="none"
              stroke={path.color}
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
            {data.points.map((point, i) => {
              if (!point) return null;
              const yValue = point[path.type as PagesReadGraphType] || 0;
              return (
                <React.Fragment key={`${path.type}-${i}`}>
                  <circle
                    cx={domainScale(parsedDateToDate(point.label).getTime())}
                    cy={rangeScale(yValue)}
                    r="5"
                    fill="white"
                    stroke={path.color}
                    strokeWidth="3"
                  />
                </React.Fragment>
              );
            })}
          </g>
        );
      })}
    </g>
  );
}

function PagesReadSvg({
  data,
  visibleStats,
  colors,
}: {
  data: StatsSerializersPagesRead;
  visibleStats: PagesReadGraphType[];
  colors: Record<string, { color: string; mono: string }>;
}) {
  const { width, height, domain } = useLayerCake();

  if (!data?.points || data.points.length === 0) return null;

  const formatTick = useMemo(() => {
    return tickFormatForDateDomain(domain);
  }, [domain]);

  return (
    <svg
      width="100%"
      height="100%"
      viewBox={`0 0 ${width} ${height}`}
      preserveAspectRatio="none"
      className="overflow-visible"
    >
      <AxisX
        ticks={domain}
        baseline
        tickMarks
        snapTicks
        formatTick={formatTick}
      />
      <PagesReadGraphLines
        data={data}
        colors={colors}
        visibleStats={visibleStats}
      />
      <Tooltip points={data.points} />
    </svg>
  );
}

function PagesReadGraph({
  data,
  visibleStats,
  colors,
}: {
  data: StatsSerializersPagesRead;
  visibleStats: PagesReadGraphType[];
  colors: Record<string, { color: string; mono: string }>;
}) {
  if (!data?.points || data.points.length === 0) return null;

  const padding = { bottom: 30, top: 0, left: 0, right: 0 };

  const domain: [number, number] = useMemo(() => {
    const startDate = parsedDateToDate(data.points[0]?.label || "");
    const endDate = parsedDateToDate(
      data.points[data.points.length - 1]?.label || ""
    );
    return [startDate.getTime(), endDate.getTime()];
  }, [data.points]);

  const range: [number, number] = useMemo(() => {
    return [0, data.maxPages || 0];
  }, [data.maxPages]);

  return (
    <LayerCake
      maxHeight={200}
      width={300}
      height={230}
      domain={domain}
      range={range}
      padding={padding}
      points={data.points}
    >
      <PagesReadSvg data={data} colors={colors} visibleStats={visibleStats} />
      <TooltipText visibleStats={visibleStats} />
    </LayerCake>
  );
}

function setInitialStatOptions(
  data: StatsSerializersPagesRead
): PagesReadGraphType[] {
  if (!data) return [];

  const options: PagesReadGraphType[] = [];
  if (data.totalPagesRead && data.totalPagesRead > 0) {
    options.push("pagesRead");
  }
  if (data.totalHoursListened && data.totalHoursListened > 0) {
    options.push("hoursAsPages");
  }
  if (options.length > 1) {
    options.push("totalPages");
  }
  return options;
}

const PagesReadStat: React.FC<PagesReadStatProps> = ({ size }) => {
  const { user, isOwner, stats } = usePage<UserStatsPageProps>().props;
  const { pagesRead: data } = stats;
  const { getTheme, getMonotheme } = useChartTheme();
  const [visibleStats, setVisibleStats] = useState<PagesReadGraphType[]>(
    setInitialStatOptions(data)
  );

  if (!data || !data.points || data.points.length === 0) {
    return (
      <StatWrapper title="Pages Read and Listened" size={size}>
        <div className="flex flex-col w-full gap-2">
          <Heading level={3}>
            {isOwner ? "You haven't" : `${user.name} hasn't`} read any pages yet
          </Heading>
        </div>
      </StatWrapper>
    );
  }

  const colors = {
    pagesRead: {
      color: getTheme(1),
      mono: getMonotheme(1),
    },
    hoursAsPages: {
      color: getTheme(4),
      mono: getMonotheme(4),
    },
    totalPages: {
      color: getTheme(3),
      mono: getMonotheme(3),
    },
  };

  function toggleVisibleState(type: PagesReadGraphType) {
    if (visibleStats.includes(type)) {
      setVisibleStats(visibleStats.filter((stat) => stat !== type));
    } else {
      setVisibleStats([...visibleStats, type]);
    }
  }

  const graphColors = {
    pagesRead: colors.pagesRead,
    hoursAsPages: colors.hoursAsPages,
    totalPages: colors.totalPages,
  };

  return (
    <StatWrapper title="Pages Read and Listened" size={size}>
      <div className="flex flex-col w-full gap-2">
        <div className="flex justify-between items-end">
          <Heading level={3}>
            {isOwner ? "You've" : user.name} read{" "}
            <LocaleNumber number={data.totalPagesAndHoursAsPages || 0} /> Pages
          </Heading>
        </div>
        <ul className="list-style-none flex flex-row gap-4 text-gray-500">
          {data.totalPagesRead && data.totalPagesRead > 0 ? (
            <li>
              <button
                type="button"
                onClick={() => toggleVisibleState("pagesRead")}
              >
                <span
                  className={`w-3 h-3 inline-block rounded-full mr-1`}
                  style={{
                    backgroundColor: visibleStats.includes("pagesRead")
                      ? colors.pagesRead.color
                      : colors.pagesRead.mono,
                  }}
                />
                <Text variant="secondary">
                  <LocaleNumber number={data.totalPagesRead} /> Pages{" "}
                </Text>
              </button>
            </li>
          ) : null}
          <li>
            <button
              type="button"
              onClick={() => toggleVisibleState("hoursAsPages")}
            >
              <span
                className={`w-3 h-3 inline-block rounded-full mr-1`}
                style={{
                  backgroundColor: visibleStats.includes("hoursAsPages")
                    ? colors.hoursAsPages.color
                    : colors.hoursAsPages.mono,
                }}
              />
              <Text variant="secondary">
                <LocaleNumber number={data.totalHoursListened || 0} /> Hours (or
                about{" "}
                <LocaleNumber number={data.totalHoursListenedAsPages || 0} />{" "}
                Pages)
              </Text>
            </button>
          </li>
          <li>
            <button
              type="button"
              onClick={() => toggleVisibleState("totalPages")}
            >
              <span
                className={`w-3 h-3 inline-block rounded-full mr-1`}
                style={{
                  backgroundColor: visibleStats.includes("totalPages")
                    ? colors.totalPages.color
                    : colors.totalPages.mono,
                }}
              />
              <Text variant="secondary">
                <LocaleNumber number={data.totalPagesAndHoursAsPages || 0} />{" "}
                Calculated Pages
              </Text>
            </button>
          </li>
        </ul>
        <Text variant="secondary" as="p" size="sm" className="mb-4">
          Highest: <LocaleNumber number={data.maxPages || 0} /> pages
        </Text>
        <PagesReadGraph
          data={data}
          colors={graphColors}
          visibleStats={visibleStats}
        />
      </div>
    </StatWrapper>
  );
};

export default PagesReadStat;
